import { Seo } from "@/components/Seo";
import { Reveal, SectionTitle, Hairline, StatusBadge } from "@/components/primitives";
import { StrategicAssetsNav } from "@/components/StrategicAssetsNav";
import { InquiryForm } from "@/components/InquiryForm";

const KINDS = [
  { t: "Foreclosure Surplus / Excess Proceeds", d: "A foreclosure or tax sale may leave proceeds subject to claims by a former owner or lienholder. Evaluating a possible entitlement requires attention to the sale records, competing interests, and applicable procedure." },
  { t: "State Unclaimed-Property Recovery", d: "State unclaimed-property programs hold reported property or funds under a separate recovery process. Their claim requirements and timelines must be examined independently of foreclosure-surplus matters." },
];

const BEFORE = [
  "You believe foreclosure surplus funds or overages may exist in connection with a property or estate matter",
  "You are prepared to provide accurate property and contact information",
  "Recovery timelines and outcomes vary based on jurisdiction and procedural status",
  "Submission does not guarantee engagement, recovery, or claim viability",
];

export default function SARecovery() {
  return (
    <div className="glow-navy jmh-page">
      <Seo title="Recovery — Strategic Assets" description="Recovery — a Strategic Assets capability (in development) focused on structured foreclosure-surplus / excess-proceeds and state unclaimed-property recovery matters. A Jennae Michelle Holdings portfolio company." />
      <StrategicAssetsNav />

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <StatusBadge status="In development" className="mb-5" />
          <h1 className="font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Recovery</h1>
          <p className="mt-2 font-display uppercase tracking-luxe text-[10px] text-[var(--muted)]">A Strategic Assets Capability · Jennae Michelle Holdings</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 mx-auto corner-frame px-8 py-10 jmh-content-shell">
            <p className="font-serif2 text-xl md:text-2xl text-[var(--bone)]/90 leading-relaxed">
              Recovery begins by determining whether an asset, proceeds, or financial interest remains available and who may be entitled to claim it. The intended process preserves supporting evidence, examines ownership, and identifies the procedural obligations relevant to each potential claim. This capability remains in development.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle tone="bone" testid="rec-kinds-title">Distinct Recovery Types</SectionTitle></Reveal>
          <div className="mt-10 jmh-content-grid">
            {KINDS.map((k, i) => (
              <Reveal key={k.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-xl text-[var(--bone)]">{k.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{k.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle tone="bone" testid="rec-before-title">Before Proceeding</SectionTitle></Reveal>
          <ul className="mt-10 space-y-5">
            {BEFORE.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex gap-4 text-[var(--bone)]/90">
                  <span className="text-[var(--gold)] mt-1">•</span>
                  <span className="font-display uppercase tracking-wide2 text-xs md:text-sm leading-relaxed">{b}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Hairline className="my-12 opacity-60" />
        </div>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle testid="rec-initiate-title">Confidential Inquiry</SectionTitle></Reveal>
          <p className="mt-4 mb-10 text-center font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)]">This capability remains under development. An inquiry may be reviewed, but submission does not establish an engagement.</p>
          <Reveal delay={0.05}>
            <InquiryForm type="recovery" division="Strategic Assets — Recovery" cta="Submit a Confidential Inquiry" requireAck testid="rec-form" note="All submissions are reviewed confidentially and at our discretion. Submission does not guarantee engagement, recovery, or claim viability." />
          </Reveal>
        </div>
      </section>

      <div className="pb-24 pt-4 text-center px-6">
        <p className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)] leading-loose mx-auto">
          Recovery is in development and is not currently accepting clients. Strategic Assets does not hold client funds, does not act as an escrow agent, and does not guarantee recovery or outcomes.
        </p>
      </div>
    </div>
  );
}
